import { useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import type { TypedDocumentString } from 'graphql-request';
import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import type { RouteSort } from '~/hooks/usePaginatedQuery';
import type {
  CatalogLengthsQuery,
  InputMaybe,
  PageInput,
  Paging,
} from '~/graphql/graphql';

export interface ProductLengthFilters {
  platformProductId?: string;
  search?: string;
}

type PagedLengths<TOption> = {
  lengths: { content: TOption[]; paging: Paging };
};

type PagedLengthsVariables = {
  platformProductId: string;
  search?: InputMaybe<string>;
  pageInput: PageInput;
};

/**
 * Pages one source of product lengths for `usePaginatedQuery`: gives every option an `id` of
 * its length id, which that hook needs to merge pages and which an option has no top-level
 * field for.
 */
export function usePagedLengths<TOption extends { length: { id: string } }>(
  document: TypedDocumentString<PagedLengths<TOption>, PagedLengthsVariables>,
  queryKey: string,
  page: number,
  pageSize: number,
  filters?: ProductLengthFilters,
  options?: { enabled?: boolean }
) {
  const { platformProductId, search } = filters ?? {};

  const result = useGraphqlQuery(
    document,
    [queryKey, platformProductId ?? '', search ?? '', page, pageSize],
    {
      platformProductId: platformProductId as string,
      search,
      pageInput: { page, size: pageSize },
    },
    {
      enabled: !!platformProductId && (options?.enabled ?? true),
      placeholderData: keepPreviousData,
    }
  );

  const data = useMemo(() => {
    const paged = result.data?.lengths;
    if (!paged) return undefined;
    return {
      lengths: {
        content: paged.content.map((candidate) => ({
          ...candidate,
          id: candidate.length.id,
        })),
        paging: paged.paging,
      },
    };
  }, [result.data]);

  return { ...result, data };
}

export type CatalogLengthOption =
  CatalogLengthsQuery['lengths']['content'][number] & { id: string };

const catalogLengthsQuery = graphql(`
  query CatalogLengths(
    $platformProductId: ID!
    $search: String
    $pageInput: PageInput!
  ) {
    lengths: catalogLengths(
      platformProductId: $platformProductId
      search: $search
      pageInput: $pageInput
    ) {
      content {
        length {
          id
          displayValue
          actualLengthInches
        }
        platformProductLength {
          id
          isNominal
          length {
            nominalLengthInches
            actualLengthInches
          }
          platformProduct {
            nominalWidthInches
            actualWidthInches
            nominalThicknessInches
            actualThicknessInches
            actualLengthInches
            piecesPerBundle {
              value
              unit
            }
          }
        }
      }
      paging {
        number
        size
        totalElements
        totalPages
      }
    }
  }
`);

/**
 * Pages a product's own purchasable lengths, shaped like `useCannedTallyCandidateLengths`
 * so `ProductLengthPicker` reads either source.
 */
export const useCatalogLengths = (
  page: number,
  pageSize: number,
  filters?: ProductLengthFilters,
  _sort?: RouteSort,
  options?: { enabled?: boolean }
) =>
  usePagedLengths(
    catalogLengthsQuery,
    'catalogLengths',
    page,
    pageSize,
    filters,
    options
  );
